import React from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpenCheck, Calculator, Languages, Medal, School, Shapes, type LucideIcon } from 'lucide-react';
import { cn } from 'lib/utils';
import { RESULT_ACCENTS, type ResultCategoryKey } from './accents';
import { toMinutes, CEFR_LADDER, cefrStepState, MILLIY_LADDER, type CategoryResultData, type DtmPoints } from './categoryResult';
import type { AttemptDelta } from './attemptDelta';
import { formatDelta } from './attemptDelta';
import { BigFigure, Chip, Eyebrow, HeroTile, Meter, ResultHero } from './primitives';

const WATERMARK: Record<ResultCategoryKey, LucideIcon> = {
  ielts: BookOpenCheck,
  sat: BookOpenCheck,
  dtm: Calculator,
  attestatsiya: School,
  milliy: Medal,
  umumiy: Shapes,
  tillar: Languages,
};

interface CategoryResultHeroProps {
  category: ResultCategoryKey;
  data: CategoryResultData;
  /** The category's own grade: "B+", "1-toifa". Null when it would only repeat the percentage. */
  levelLabel?: string | null;
  passed?: boolean | null;
  /** DTM only. */
  dtm?: DtmPoints | null;
  /** The subject a single DTM test covers. */
  subjectTitle?: string;
  /** The learner's goal. */
  goal?: { target: string } | null;
  /** How this attempt moved from the previous one. */
  delta?: AttemptDelta | null;
  /** Milliy only: the test's own 0-100 score (points earned over possible). Null leaves the share correct. */
  ball?: number | null;
  className?: string;
}

const fmt1 = (n: number) => n.toFixed(1);

function Ring({ percent, label }: { percent: number; label: string }) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  return (
    <div className="relative mt-3 h-[168px] w-[168px]">
      <svg viewBox="0 0 128 128" className="h-full w-full -rotate-90" aria-hidden>
        <circle cx="64" cy="64" r={radius} fill="none" stroke="#fff" strokeWidth="12" />
        <circle
          cx="64"
          cy="64"
          r={radius}
          fill="none"
          stroke="var(--ra-fg)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={`${(percent / 100) * circumference} ${circumference}`}
        />
      </svg>
      <span className="absolute inset-0 flex flex-col items-center justify-center">
        <b className="font-display text-[44px] font-extrabold leading-none tracking-[-0.05em] tabular-nums">{percent}%</b>
        <span className="mt-1 text-xs font-medium text-[#4B5468]">{label}</span>
      </span>
    </div>
  );
}

/** The hero of a DTM, Milliy, Attestatsiya, Umumiy or Tillar result: the grade the category cares about, then the counts. */
export function CategoryResultHero({ category, data, levelLabel, passed, dtm, subjectTitle, goal, delta, ball, className }: CategoryResultHeroProps) {
  const { t } = useTranslation();
  const accent = RESULT_ACCENTS[category];
  const Watermark = WATERMARK[category];
  const minutes = toMinutes(data.answeredSeconds);
  // A language test reads "CEFR B1"; the ladder and the figure want just the level.
  const cefrLevel = levelLabel ? levelLabel.replace(/^CEFR\s+/i, '') : null;
  const share = (n: number) => (data.total ? (n / data.total) * 100 : 0);

  const tiles = (
    <div className="grid grid-cols-2 gap-2.5 lg:gap-3">
      <HeroTile label={t('result.view.correct')} value={data.correct} detail={`/ ${data.total}`} percent={share(data.correct)} />
      <HeroTile label={t('result.view.wrong')} value={data.wrong} percent={share(data.wrong)} />
      <HeroTile label={t('result.view.skipped')} value={data.skipped} percent={share(data.skipped)} />
      <HeroTile label={t('result.view.time')} value={minutes > 0 ? minutes : '—'} detail={minutes > 0 ? t('result.view.min') : undefined} />
    </div>
  );

  const aside =
    category === 'milliy' ? (
      <div className="space-y-3">
        <ol aria-label={t('result.view.milliy_ladder')} className="grid grid-cols-6 gap-1.5">
          {MILLIY_LADDER.map((step) => {
            const reached = data.percent >= step.min;
            const here = levelLabel === step.label;
            return (
              <li
                key={step.label}
                aria-current={here ? 'step' : undefined}
                className={cn(
                  'grid h-12 place-items-center rounded-xl border-2 font-display text-[15px] font-extrabold',
                  here
                    ? 'border-[color:var(--ra-fg)] bg-[color:var(--ra-fg)] text-white'
                    : reached
                      ? 'border-white bg-white text-[color:var(--ra-fg)]'
                      : 'border-white/70 bg-white/40 text-[#646D84]',
                )}
              >
                {step.label}
              </li>
            );
          })}
        </ol>
        {tiles}
      </div>
    ) : category === 'tillar' ? (
      <div className="space-y-3">
        <ol aria-label={t('result.view.cefr_ladder')} className="grid grid-cols-6 gap-1.5">
          {CEFR_LADDER.map((step) => {
            const state = cefrStepState(step, cefrLevel, goal?.target);
            return (
              <li
                key={step}
                aria-current={state === 'here' ? 'step' : undefined}
                className={cn(
                  'grid h-12 place-items-center rounded-xl border-2 font-display text-[15px] font-extrabold',
                  state === 'here' && 'border-[color:var(--ra-fg)] bg-[color:var(--ra-fg)] text-white',
                  state === 'passed' && 'border-white bg-white text-[color:var(--ra-fg)]',
                  state === 'target' && 'border-dashed border-[color:var(--ra-fg)] bg-white/40 text-[color:var(--ra-fg)]',
                  state === 'ahead' && 'border-white/70 bg-white/40 text-[#646D84]',
                )}
              >
                {step}
              </li>
            );
          })}
        </ol>
        {tiles}
      </div>
    ) : (
      tiles
    );

  const eyebrow =
    category === 'dtm'
      ? subjectTitle
        ? `${subjectTitle} · ${t('result.view.dtm_price')}`
        : t('result.view.total_points')
      : category === 'milliy' || category === 'tillar'
        ? t('result.view.your_level')
        : category === 'attestatsiya'
          ? t('result.view.overall_result')
          : t('result.view.share_correct');

  return (
    <ResultHero
      accent={accent}
      label={t('result.view.hero_label')}
      watermark={<Watermark className="h-full w-full" strokeWidth={0.6} />}
      aside={aside}
      className={className}
    >
      <Eyebrow accent>{eyebrow}</Eyebrow>

      {category === 'dtm' && dtm ? (
        <>
          <BigFigure>{fmt1(dtm.points)}</BigFigure>
          <p className="mt-3.5 text-[13.5px] leading-relaxed text-[#364055]">
            {t('result.view.dtm_max', { correct: data.correct, total: data.total, max: fmt1(dtm.max) })}
          </p>
          <Meter percent={dtm.max ? (dtm.points / dtm.max) * 100 : 0} className="mt-3 !bg-white" />
          {goal && (
            <div className="mt-4">
              <Chip dark>{t('result.view.goal', { target: goal.target })}</Chip>
            </div>
          )}
        </>
      ) : category === 'milliy' ? (
        <>
          <BigFigure>{levelLabel ?? '—'}</BigFigure>
          <div className="mt-[18px] flex flex-wrap gap-2">
            <Chip>{ball != null ? t('result.view.ball', { value: ball }) : `${data.percent}%`}</Chip>
            {goal && <Chip dark>{t('result.view.goal', { target: goal.target })}</Chip>}
          </div>
          {ball != null && <p className="mt-3.5 text-[12.5px] leading-relaxed text-[#4B5468]">{t('result.view.ball_note')}</p>}
        </>
      ) : category === 'tillar' && cefrLevel ? (
        <>
          <BigFigure>{cefrLevel}</BigFigure>
          <div className="mt-[18px] flex flex-wrap gap-2">
            <Chip>CEFR</Chip>
            <Chip>{data.percent}%</Chip>
            {goal && <Chip dark>{t('result.view.goal', { target: goal.target })}</Chip>}
          </div>
          <p className="mt-3.5 text-[12.5px] leading-relaxed text-[#4B5468]">{t('result.view.cefr_estimate')}</p>
        </>
      ) : category === 'attestatsiya' ? (
        <>
          <BigFigure>{data.percent}%</BigFigure>
          <div className="mt-[18px] flex flex-wrap gap-2">
            {passed != null && <Chip dark={passed}>{passed ? t('result.view.above_pass') : t('result.view.below_pass')}</Chip>}
            {levelLabel && <Chip>{levelLabel}</Chip>}
            {goal && <Chip>{t('result.view.goal', { target: goal.target })}</Chip>}
          </div>
          <p className="mt-3.5 text-[13.5px] text-[#364055]">{t('result.view.correct_of_total', { correct: data.correct, total: data.total })}</p>
        </>
      ) : (
        <>
          <Ring percent={data.percent} label={t('result.view.correct_answers')} />
          {goal && (
            <div className="mt-4">
              <Chip dark>{t('result.view.goal', { target: goal.target })}</Chip>
            </div>
          )}
        </>
      )}
      {delta && (
        <div className="mt-3">
          <Chip>{t('result.view.vs_previous', { delta: formatDelta(delta) })}</Chip>
        </div>
      )}
    </ResultHero>
  );
}
